import * as THREE from 'three';
import {SketchLineObject, SketchPointObject} from '../../mesh/sketch';
import {SKETCH_GEOMETRY_TOLERANCE_CONFIG} from '../config';

/**
 * 草图公共点判断容差。
 */
export const SKETCH_CLOSE_TOLERANCE = SKETCH_GEOMETRY_TOLERANCE_CONFIG.collectionClosurePosition;

/**
 * 草图几何计算容差。
 */
export const SKETCH_GEOMETRY_TOLERANCE = 1e-6;

/**
 * 草图角点端点角色枚举。
 * 说明：
 * 1. 仅用于描述“当前线段哪个端点作为角点端”；
 * 2. 不使用 `end` 作为枚举项命名，统一使用 `Terminal`；
 * 3. 该角色会参与集合插入位次与端点替换逻辑。
 */
export enum SketchCornerPointRole {
  /** 起始端。 */
  Start = 'start',
  /** 末端。 */
  Terminal = 'terminal',
}

/**
 * 草图圆角/倒角操作选项。
 */
export interface SketchCornerOperationOptions {
  /** 圆角半径。 */
  radius?: number;
  /** 是否对每个圆角创建尺寸。 */
  dimensionEachFillet?: boolean;
  /** 倒角距离 1。 */
  distance1?: number;
  /** 倒角距离 2。 */
  distance2?: number;
  /** 倒角角度。 */
  angleDeg?: number;
  /** 倒角类型。 */
  chamferType?: 'angle-distance' | 'distance-distance' | 'equal-distance';
}

/**
 * 草图线段解析结果。
 */
export interface SketchCornerLineData {
  /** 草图线对象。 */
  line: SketchLineObject;
  /** 起点控制点。 */
  startPoint: SketchPointObject;
  /** 终点控制点。 */
  endPoint: SketchPointObject;
  /** 起点世界坐标。 */
  startWorld: THREE.Vector3;
  /** 终点世界坐标。 */
  endWorld: THREE.Vector3;
  /** 起点局部坐标。 */
  startLocal: THREE.Vector2;
  /** 终点局部坐标。 */
  endLocal: THREE.Vector2;
  /** 线段长度。 */
  length: number;
  /** 是否为构造线。 */
  isConstruction: boolean;
}

/**
 * 草图线段在角点上的使用信息。
 */
export interface SketchCornerLineUsage {
  /** 源线段数据。 */
  lineData: SketchCornerLineData;
  /** 角点控制点。 */
  cornerPoint: SketchPointObject;
  /** 角点所在端点角色。 */
  cornerRole: SketchCornerPointRole;
  /** 角点世界坐标。 */
  cornerWorld: THREE.Vector3;
  /** 远端控制点。 */
  farPoint: SketchPointObject;
  /** 远端世界坐标。 */
  farWorld: THREE.Vector3;
  /** 角点局部坐标。 */
  cornerLocal: THREE.Vector2;
  /** 远端局部坐标。 */
  farLocal: THREE.Vector2;
  /** 从角点指向线段内部的局部单位方向。 */
  directionLocal: THREE.Vector2;
  /** 从角点到远端的距离。 */
  farDistanceToCorner: number;
}

/**
 * 草图公共点解析结果。
 */
export interface SketchSharedCornerPoint {
  /** 公共点控制点。 */
  sharedPoint: SketchPointObject;
  /** 第一条线的公共点端点角色。 */
  firstCornerRole: SketchCornerPointRole;
  /** 第二条线的公共点端点角色。 */
  secondCornerRole: SketchCornerPointRole;
  /** 公共点世界坐标。 */
  cornerWorld: THREE.Vector3;
  /** 公共点局部坐标。 */
  cornerLocal: THREE.Vector2;
}

/**
 * 草图角点解析结果。
 */
export interface SketchCornerAnalysis {
  /** 角点世界坐标。 */
  cornerWorld: THREE.Vector3;
  /** 角点局部坐标。 */
  cornerLocal: THREE.Vector2;
  /** 第一条线的角点使用信息。 */
  firstUsage: SketchCornerLineUsage;
  /** 第二条线的角点使用信息。 */
  secondUsage: SketchCornerLineUsage;
}

/**
 * 草图圆角/倒角执行结果。
 */
export interface SketchCornerOperationResult {
  /** 是否成功。 */
  success: boolean;
  /** 提示消息。 */
  message: string;
  /** 新创建实体。 */
  createdObject?: THREE.Line;
  /** 相关控制点。 */
  controlPoints?: SketchPointObject[];
}

/**
 * 草图圆角几何求解结果。
 */
export interface SketchFilletGeometryResult {
  /** 第一条线的修剪点局部坐标。 */
  trimPoint1Local: THREE.Vector2;
  /** 第二条线的修剪点局部坐标。 */
  trimPoint2Local: THREE.Vector2;
  /** 第一条线的修剪点世界坐标。 */
  trimPoint1World: THREE.Vector3;
  /** 第二条线的修剪点世界坐标。 */
  trimPoint2World: THREE.Vector3;
  /** 圆心局部坐标。 */
  centerLocal: THREE.Vector2;
  /** 圆心世界坐标。 */
  centerWorld: THREE.Vector3;
  /** 起始角。 */
  startAngle: number;
  /** 结束角。 */
  endAngle: number;
  /** 是否顺时针。 */
  clockwise: boolean;
  /** 圆弧采样点。 */
  arcPoints: THREE.Vector3[];
}

